import { ReactNode, useState } from 'react';
import { LockKeyhole, MessageSquare, Sparkles } from 'lucide-react';
import { Card } from '@/components/ui/Card';
import { Input } from '@/components/ui/Input';
import { Button } from '@/components/ui/Button';
import { registrationApi } from '@/services/api';
import { RegistrationType } from '@/types';

interface EnquiryPanelProps {
  itemType: RegistrationType;
  itemId: string;
  sectionLabel?: string;
  heading?: ReactNode;
  headingCaption?: string;
  headingVariant?: 'default' | 'featured';
  description?: string;
  ctaLabel: string;
  submitLabel?: string;
  notesLabel?: string;
  notesPlaceholder?: string;
  showNotesField?: boolean;
  showPrivacyNotes?: boolean;
  hideFieldLabels?: boolean;
  revealFieldsOnCta?: boolean;
  phoneFieldMode?: 'default' | 'india-mobile';
  extraInfo?: {
    label: string;
    value: ReactNode;
    description?: string;
  };
}

const normalizeIndianPhoneDigits = (value: string) => value.replace(/\D/g, '').slice(0, 10);

const isValidIndianMobile = (value: string) => /^[6-9]\d{9}$/.test(value);

export const EnquiryPanel = ({
  itemType,
  itemId,
  sectionLabel = 'Learning Guidance',
  heading,
  headingCaption,
  headingVariant = 'default',
  description,
  ctaLabel,
  submitLabel = 'Submit',
  notesLabel = 'What would you like help with? (optional)',
  notesPlaceholder = 'Share goals, preferred schedule, age group, or any questions you have.',
  showNotesField = true,
  showPrivacyNotes = true,
  hideFieldLabels = false,
  revealFieldsOnCta = false,
  phoneFieldMode = 'default',
  extraInfo,
}: EnquiryPanelProps) => {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [notes, setNotes] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isFormVisible, setIsFormVisible] = useState(!revealFieldsOnCta);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const isIndiaMobileMode = phoneFieldMode === 'india-mobile';
  const normalizedPhone = isIndiaMobileMode ? normalizeIndianPhoneDigits(phone) : phone.trim();
  const isPhoneValid = isIndiaMobileMode ? isValidIndianMobile(normalizedPhone) : Boolean(normalizedPhone);
  const canSubmit = Boolean(name.trim() && email.trim() && isPhoneValid);
  const showCombinedPrivacyNote = showPrivacyNotes && isFormVisible;
  const enquirySubject = itemType === 'course' ? 'this course' : itemType === 'sports' ? 'this sports program' : 'this event';

  const handleSubmit = async () => {
    setError('');
    setSuccessMessage('');

    if (revealFieldsOnCta && !isFormVisible) {
      setIsFormVisible(true);
      return;
    }

    if (!canSubmit) {
      setError(
        isIndiaMobileMode
          ? 'Please enter your name, email address, and a valid 10-digit Indian mobile number.'
          : 'Please fill in your name, email address, and phone number before submitting.'
      );
      return;
    }

    try {
      setIsSubmitting(true);
      await registrationApi.create({
        itemType,
        itemId,
        name: name.trim(),
        email: email.trim(),
        phone: normalizedPhone,
        notes: notes.trim(),
      });

      setSuccessMessage(
        'Thank you for reaching out. Our team will contact you within the next 12 hours to guide you on the next steps.'
      );
      setName('');
      setEmail('');
      setPhone('');
      setNotes('');
      setIsFormVisible(!revealFieldsOnCta);
    } catch (submitError) {
      setError((submitError as Error).message);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Card className="overflow-hidden rounded-[1.75rem] border-white/70 bg-white/96 p-6 shadow-[0_24px_60px_rgba(15,23,42,0.08)] sm:p-7">
      <div className="flex items-center gap-3">
        <span className="inline-flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-[rgb(var(--accent-solid))/0.1] text-[rgb(var(--accent-solid))]">
          <MessageSquare className="h-5 w-5" />
        </span>
        <p className="text-xs font-semibold uppercase tracking-[0.22em] text-[rgb(var(--accent-solid))]">{sectionLabel}</p>
      </div>
      {heading ? (
        headingVariant === 'featured' ? (
          <div className="mt-5 overflow-hidden rounded-[1.5rem] border border-slate-200/80 bg-[linear-gradient(135deg,rgba(var(--accent-solid),0.12),rgba(255,255,255,0.96)_58%,rgba(15,23,42,0.04))] p-5 shadow-[inset_0_1px_0_rgba(255,255,255,0.72)]">
            {headingCaption ? (
              <p className="text-[0.7rem] font-semibold uppercase tracking-[0.22em] text-slate-500">{headingCaption}</p>
            ) : null}
            <div className="mt-3 flex flex-wrap items-end justify-between gap-3">
              <h3 className="font-display text-3xl font-semibold tracking-tight text-slate-950 sm:text-[2.2rem]">{heading}</h3>
              <span className="rounded-full border border-white/80 bg-white/80 px-3 py-1 text-[0.7rem] font-semibold uppercase tracking-[0.14em] text-slate-600">
                Admissions guidance available
              </span>
            </div>
            {description ? <p className="mt-4 text-sm leading-6 text-slate-600">{description}</p> : null}
          </div>
        ) : (
          <>
            <h3 className="mt-4 text-2xl font-semibold tracking-tight text-slate-950">{heading}</h3>
            {description ? <p className="mt-3 text-sm leading-6 text-slate-600">{description}</p> : null}
          </>
        )
      ) : description ? (
        <p className="mt-3 text-sm leading-6 text-slate-600">{description}</p>
      ) : null}

      <div className="mt-6 rounded-[1.5rem] border border-emerald-100 bg-emerald-50/85 p-4 text-sm leading-6 text-emerald-800">
        <div className="flex items-start gap-2">
          <Sparkles className="mt-0.5 h-4 w-4 shrink-0" />
          <p>
            You can enquire first and talk with our team before making any payment. We will help you choose the right
            option, answer your questions, and guide you through the next steps.
          </p>
        </div>
      </div>

      {extraInfo ? (
        <div className="mt-5 rounded-[1.5rem] border border-slate-200 bg-slate-50/90 p-5">
          <p className="text-xs font-semibold uppercase tracking-[0.22em] text-slate-500">{extraInfo.label}</p>
          <p className="mt-3 text-2xl font-semibold tracking-tight text-slate-950">{extraInfo.value}</p>
          {extraInfo.description ? <p className="mt-2 text-sm leading-6 text-slate-600">{extraInfo.description}</p> : null}
        </div>
      ) : null}

      {successMessage ? (
        <p className="status-message mt-6 border-green-100 bg-green-50 text-green-700">{successMessage}</p>
      ) : (
        <div className="mt-6 space-y-4">
          {isFormVisible ? (
            <>
              <Input
                label={hideFieldLabels ? undefined : 'Your Name'}
                aria-label="Your name"
                value={name}
                onChange={(event) => setName(event.target.value)}
                placeholder="Your name"
              />
              <div className="space-y-2">
                <Input
                  label={hideFieldLabels ? undefined : 'Email Address'}
                  aria-label="Email address"
                  type="email"
                  value={email}
                  onChange={(event) => setEmail(event.target.value)}
                  placeholder={hideFieldLabels ? 'Email address' : 'name@example.com'}
                />
              </div>
              <div className="space-y-2">
                {isIndiaMobileMode ? (
                  <div className="w-full">
                    {!hideFieldLabels ? <label className="form-label">Mobile Number</label> : null}
                    <div className="flex overflow-hidden rounded-2xl border border-slate-300 bg-white shadow-sm transition focus-within:border-[rgb(var(--accent-solid))] focus-within:ring-4 focus-within:ring-[rgb(var(--accent-solid))/0.12]">
                      <span className="inline-flex min-h-11 items-center border-r border-slate-200 bg-slate-50 px-4 text-sm font-semibold text-slate-700">
                        +91
                      </span>
                      <input
                        type="tel"
                        inputMode="numeric"
                        autoComplete="tel-national"
                        pattern="[6-9][0-9]{9}"
                        maxLength={10}
                        value={phone}
                        onChange={(event) => setPhone(normalizeIndianPhoneDigits(event.target.value))}
                        placeholder={hideFieldLabels ? '10-digit mobile number' : '9876543210'}
                        aria-label="Indian mobile number"
                        className="min-h-11 w-full bg-transparent px-4 py-3 text-slate-950 outline-none placeholder:text-slate-400"
                      />
                    </div>
                    {!hideFieldLabels ? (
                      <p className="mt-1.5 text-xs text-slate-500">Enter a 10-digit Indian mobile number.</p>
                    ) : null}
                  </div>
                ) : (
                  <Input
                    label={hideFieldLabels ? undefined : 'Phone Number'}
                    aria-label="Phone number"
                    type="tel"
                    autoComplete="tel"
                    value={phone}
                    onChange={(event) => setPhone(event.target.value)}
                    placeholder="Phone number"
                  />
                )}
              </div>
              {showNotesField ? (
                <div>
                  {notesLabel ? <label className="form-label">{notesLabel}</label> : null}
                  <textarea
                    value={notes}
                    onChange={(event) => setNotes(event.target.value)}
                    placeholder={notesPlaceholder}
                    aria-label={notesLabel || 'Additional details'}
                    className="form-control min-h-[128px]"
                  />
                </div>
              ) : null}
            </>
          ) : null}

          {error ? <p className="status-message border-red-100 bg-red-50 text-red-700">{error}</p> : null}

          <Button type="button" className="w-full" onClick={handleSubmit} disabled={isSubmitting}>
            {isSubmitting ? 'Submitting...' : isFormVisible ? submitLabel : ctaLabel}
          </Button>

          {showCombinedPrivacyNote ? (
            <div className="rounded-[1.25rem] border border-slate-200 bg-slate-50/85 px-4 py-3">
              <div className="flex items-start gap-2 text-xs leading-5 text-slate-500">
                <LockKeyhole className="mt-0.5 h-3.5 w-3.5 shrink-0" />
                <div>
                  <p>Your details are private.</p>
                  <p>We use them only to contact you about {enquirySubject}.</p>
                  <p>We will not share them with anyone else.</p>
                </div>
              </div>
            </div>
          ) : null}
        </div>
      )}
    </Card>
  );
};
